import { cacheGet, cachePut, enqueue, isQueueable } from './offline';

const TOKEN_KEY = 'rq_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY) || '';
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

/** Raised on a 401 so the router can bounce to login without every caller caring. */
export class UnauthorizedError extends Error {
  constructor() { super('unauthorized'); this.name = 'UnauthorizedError'; }
}

/** A mutation that couldn't reach the server and is now queued for replay. */
export class QueuedError extends Error {
  constructor() {
    super('Saved on this device — it will sync when you’re back online.');
    this.name = 'QueuedError';
    this.queued = true;
  }
}

/** Served from the local cache because the network was unreachable. */
export class StaleData {
  constructor(data, at) { this.data = data; this.at = at; }
}

async function request(path, { method = 'GET', body, auth = true, allowCache = true } = {}) {
  let res;
  try {
    res = await fetch('/api' + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(auth && getToken() ? { Authorization: 'Bearer ' + getToken() } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Network-level failure only — fetch does not reject on 4xx/5xx.
    if (method === 'GET' && allowCache) {
      const hit = await cacheGet(path);
      if (hit) return hit.data;
      throw new Error('You’re offline and this hasn’t been downloaded yet.');
    }
    if (auth && isQueueable(path) && ['POST', 'PUT', 'PATCH'].includes(method)) {
      try {
        await enqueue({ method, path, body });
        throw new QueuedError();
      } catch (qErr) {
        if (qErr instanceof QueuedError) throw qErr;
        // Queueing itself failed (IndexedDB unavailable/stuck) — fall through
        // to the plain offline message below instead of hanging or crashing.
      }
    }
    throw new Error('You’re offline — this change needs a connection.');
  }

  if (res.status === 401 && auth) {
    setToken('');
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `Request failed (${res.status})`);
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  // Mirror reads so the same screen opens offline next time.
  if (method === 'GET' && allowCache && data) cachePut(path, data);

  return data;
}

export const api = {
  get: (p) => request(p),
  post: (p, body) => request(p, { method: 'POST', body }),
  put: (p, body) => request(p, { method: 'PUT', body }),
  patch: (p, body) => request(p, { method: 'PATCH', body }),
  del: (p) => request(p, { method: 'DELETE' }),
};

/**
 * Public token routes. Cached too — a customer who opens their proposal once
 * can reopen it in a basement with no signal, which is exactly where people
 * read quotes.
 */
export const publicApi = {
  get: (p) => request(p, { auth: false }),
  post: (p, body) => request(p, { method: 'POST', body, auth: false }),
};
